<template>
  <div v-if="crudManagerStore.isInitiated">
    <template
      v-for="entry in sortedEntries"
      :key="entry.key">
      <CustomMenuItem
        v-if="entry.kind === 'custom'"
        :item="entry.item" />
      <div
        v-else-if="hasInstanceAccess(entry.instance)"
        :id="`crud-instance-${entry.instance.id}`"
        :class="['parent-item', { 'is-active': isInstanceActive(entry.instance) }]">
        <InstanceMenuItem
          :instance="entry.instance"
          :has-visible-children="hasVisibleChildren(entry.instance.id)" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { customMenuItems } from 'src/CrudManager/config/customMenuItems'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudInstance } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import CustomMenuItem from './CustomMenuItem.vue'
import InstanceMenuItem from './InstanceMenuItem.vue'

const props = defineProps<{
  category: string
}>()

const { t } = useI18n()
const route = useRoute()
const crudManagerStore = useCrudManagerStore()
const { canUserCrud, generateModuleUrl } = useCrudUtils()

const hasInstanceAccess = (instance: CrudInstance) => {
  return canUserCrud(instance.modelName, 'read') && canUserCrud(instance.modelName, 'list')
}

const hasVisibleChildren = (instanceId: string) => {
  const children = crudManagerStore.crudInstances.filter((i) => i.parentId === instanceId)
  return children.some((c) => hasInstanceAccess(c))
}

const instanceUrl = (instance: CrudInstance) => {
  if (!crudManagerStore.isInitiated) return ''
  return generateModuleUrl(instance.modelName, instance.moduleName, instance.props?.modelId as string) || ''
}

// Parent is "active" only when its own URL or one of its children's URL
// matches the current route. Relying on instance.status.isVisible alone
// left the last-opened CRUD highlighted after navigating to a non-CRUD
// page (e.g. /payments), since nothing clears that flag on such routes.
const isInstanceActive = (instance: CrudInstance) => {
  const current = route.path
  if (instanceUrl(instance) === current) return true
  return crudManagerStore.crudInstances.some((i) => i.parentId === instance.id && instanceUrl(i) === current)
}

const instancesForCategory = computed(() => {
  if (!crudManagerStore.isInitiated) return []
  return crudManagerStore.crudInstances
    // external recents stamps are not panes; they live in the recents UIs only
    .filter((i) => !i.parentId && i.moduleName !== 'external')
    .filter((i) => {
      const model = crudManagerStore.getModelMeta(i.modelName)
      if (model?.disabled) return false
      return (model?.category || 'operations') === props.category
    })
})

const customMenuItemsForCategory = computed(() => customMenuItems.filter((item) => item.category === props.category))

type SidebarEntry =
  | { kind: 'custom'; key: string; label: string; item: (typeof customMenuItems)[number] }
  | { kind: 'instance'; key: string; label: string; instance: CrudInstance }

const sortedEntries = computed<SidebarEntry[]>(() => {
  const entries: SidebarEntry[] = []
  for (const item of customMenuItemsForCategory.value) {
    entries.push({ kind: 'custom', key: `custom:${item.label}`, label: t(`crudmanager.customMenu.${item.label}`), item })
  }
  for (const instance of instancesForCategory.value) {
    entries.push({
      kind: 'instance',
      key: `instance:${instance.id}`,
      label: t(`crudmodels.${instance.modelName}.name`, 2),
      instance,
    })
  }
  return entries.sort((a, b) => a.label.localeCompare(b.label))
})
</script>

<style lang="scss" scoped>
.parent-item :deep(.menu-list-item) {
  margin: 0;
}

.parent-item :deep(.menu-list-item) {
  border-radius: 8px;
}

.parent-item :deep(.q-item__label):not(.text-caption) {
  font-size: 0.844rem;
  font-weight: 450;
}

.parent-item {
  position: relative;
  border-radius: 8px;
  margin: 1px 8px;

  &:hover :deep(.menu-list-item) {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &.is-active {
    :deep(.menu-list-item) {
      background-color: color-mix(in srgb, var(--q-primary) 28%, transparent);
    }

    &::before {
      content: '';
      position: absolute;
      left: -4px;
      top: 20%;
      bottom: 20%;
      width: 3px;
      border-radius: 2px;
      background: var(--q-primary);
      z-index: 1;
    }

    :deep(.q-item),
    :deep(.q-item.q-router-link--active) {
      color: #ffffff;
    }

    :deep(.icon-instance),
    :deep(.q-item__label):not(.text-caption) {
      color: #ffffff !important;
      font-weight: 550;
    }
  }
}

.body--light {
  .parent-item :deep(.menu-list-item) {
    background-color: transparent;
  }

  .parent-item:hover :deep(.menu-list-item) {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .parent-item.is-active {
    :deep(.menu-list-item) {
      background-color: color-mix(in srgb, var(--q-primary) 14%, transparent);
    }

    :deep(.q-item),
    :deep(.q-item.q-router-link--active) {
      color: color-mix(in srgb, var(--q-primary) 38%, #000);
    }

    :deep(.icon-instance) {
      color: var(--q-primary) !important;
      opacity: 1;
    }

    :deep(.q-item__label):not(.text-caption) {
      color: color-mix(in srgb, var(--q-primary) 38%, #000) !important;
      font-weight: 600;
    }
  }
}
</style>
